import type { DateTime } from "timezonecomplete"
import type { Timeseries } from "@hydrologiq/schemas/1.0.0/installationPage"

export interface RangeGranularityResult {
  granularity: "1m" | "5m" | "10m" | "1h" | "6h" | "1d"
  timeseries: Timeseries
}

/**
 * Single range-to-granularity mapper shared by the Asset, Hybrid parent Asset
 * and System Timescale monitoring views.
 *
 * Each bracket picks a `time_bucket` granularity and the matching chart
 * `Timeseries` settings. Thresholds are strict (`>`), so a duration exactly on
 * a boundary falls into the shorter bracket. Duration is taken from the
 * canonical UTC instants, so the result is independent of display timezone.
 *
 * The requested granularity is advisory: the server's
 * `enforce_minimum_granularity()` remains authoritative.
 */
export function mapRangeGranularity(startUtc: DateTime, endUtc: DateTime): RangeGranularityResult {
  const durationMs = Math.abs(endUtc.unixUtcMillis() - startUtc.unixUtcMillis())
  const hours = durationMs / (60 * 60 * 1000)
  const days = hours / 24

  // > 60 days  →  1 day buckets
  if (days > 60) {
    return {
      granularity: "1d",
      timeseries: {
        // startResolution 90 day is deliberately unchanged: no Timescale chart reads it.
        startResolution: { quantity: 90, resolution: "day" },
        endResolution: { quantity: 2, resolution: "day" },
        interval: { quantity: 1, resolution: "day" },
        scale: {
          normal: { quantity: 7, resolution: "day" },
          mobile: { quantity: 14, resolution: "day" },
        },
      },
    }
  }

  // > 14 days and <= 60 days  →  6 hour buckets
  if (days > 14) {
    return {
      granularity: "6h",
      timeseries: {
        startResolution: { quantity: 14, resolution: "day" },
        endResolution: { quantity: 12, resolution: "hour" },
        interval: { quantity: 6, resolution: "hour" },
        scale: {
          normal: { quantity: 2, resolution: "day" },
          mobile: { quantity: 4, resolution: "day" },
        },
      },
    }
  }

  // > 7 days and <= 14 days  →  1 hour buckets
  if (days > 7) {
    return {
      granularity: "1h",
      timeseries: {
        startResolution: { quantity: 7, resolution: "day" },
        endResolution: { quantity: 4, resolution: "hour" },
        interval: { quantity: 1, resolution: "hour" },
        scale: {
          normal: { quantity: 12, resolution: "hour" },
          mobile: { quantity: 1, resolution: "day" },
        },
      },
    }
  }

  // > 3 days and <= 7 days  →  1 hour buckets
  if (days > 3) {
    return {
      granularity: "1h",
      timeseries: {
        startResolution: { quantity: 3, resolution: "day" },
        endResolution: { quantity: 1, resolution: "hour" },
        interval: { quantity: 1, resolution: "hour" },
        scale: {
          normal: { quantity: 6, resolution: "hour" },
          mobile: { quantity: 12, resolution: "hour" },
        },
      },
    }
  }

  // > 1 day and <= 3 days  →  10 minute buckets
  if (days > 1) {
    return {
      granularity: "10m",
      timeseries: {
        startResolution: { quantity: 24, resolution: "hour" },
        endResolution: { quantity: 1, resolution: "hour" },
        interval: { quantity: 10, resolution: "minute" },
        scale: {
          normal: { quantity: 2, resolution: "hour" },
          mobile: { quantity: 3, resolution: "hour" },
        },
      },
    }
  }

  // > 12 hours and <= 1 day  →  10 minute buckets
  if (hours > 12) {
    return {
      granularity: "10m",
      timeseries: {
        startResolution: { quantity: 24, resolution: "hour" },
        endResolution: { quantity: 1, resolution: "hour" },
        interval: { quantity: 10, resolution: "minute" },
        scale: {
          normal: { quantity: 2, resolution: "hour" },
          mobile: { quantity: 3, resolution: "hour" },
        },
      },
    }
  }

  // > 6 hours and <= 12 hours  →  5 minute buckets
  if (hours > 6) {
    return {
      granularity: "5m",
      timeseries: {
        startResolution: { quantity: 12, resolution: "hour" },
        endResolution: { quantity: 2, resolution: "hour" },
        interval: { quantity: 5, resolution: "minute" },
        scale: {
          normal: { quantity: 1, resolution: "hour" },
          mobile: { quantity: 2, resolution: "hour" },
        },
      },
    }
  }

  // > 3 hours and <= 6 hours  →  1 minute buckets
  if (hours > 3) {
    return {
      granularity: "1m",
      timeseries: {
        startResolution: { quantity: 6, resolution: "hour" },
        endResolution: { quantity: 15, resolution: "minute" },
        interval: { quantity: 1, resolution: "minute" },
        scale: {
          normal: { quantity: 30, resolution: "minute" },
          mobile: { quantity: 1, resolution: "hour" },
        },
      },
    }
  }

  // <= 3 hours  →  1 minute buckets
  return {
    granularity: "1m",
    timeseries: {
      startResolution: { quantity: 1, resolution: "hour" },
      endResolution: { quantity: 5, resolution: "minute" },
      interval: { quantity: 1, resolution: "minute" },
      scale: {
        normal: { quantity: 5, resolution: "minute" },
        mobile: { quantity: 30, resolution: "minute" },
      },
    },
  }
}
